import * as Sentry from "@sentry/react";
import type { ErrorEvent } from "@sentry/react";
import { processChunkLoadEvent } from "@/app/chunkLoad";
import { processThirdPartyNoiseEvent } from "@/app/thirdPartyNoise";

type SlowOperationContext = Record<string, unknown>;

const SENSITIVE_QUERY_KEYS = [
  "access_token",
  "token",
  "refresh_token",
  "id_token",
] as const;

/** Redact auth tokens that may appear in URLs / breadcrumbs / request data. */
export function scrubSensitiveUrl(url: string | undefined): string | undefined {
  if (!url || typeof url !== "string") return url;
  try {
    // Absolute or relative — URL needs a base for relative paths.
    const parsed = new URL(url, "https://sentry.invalid");
    let changed = false;
    for (const key of SENSITIVE_QUERY_KEYS) {
      if (parsed.searchParams.has(key)) {
        parsed.searchParams.set(key, "[Filtered]");
        changed = true;
      }
    }
    if (!changed) return url;
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return parsed.toString();
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return url.replace(
      /([?&](?:access_token|token|refresh_token|id_token)=)[^&]*/gi,
      "$1[Filtered]",
    );
  }
}

function scrubSentryEventUrls(event: ErrorEvent): ErrorEvent {
  if (event.request?.url) {
    event.request.url = scrubSensitiveUrl(event.request.url);
  }
  const values = event.exception?.values;
  if (values) {
    for (const value of values) {
      const frames = value.stacktrace?.frames;
      if (!frames) continue;
      for (const frame of frames) {
        if (frame.filename) {
          frame.filename = scrubSensitiveUrl(frame.filename) ?? frame.filename;
        }
      }
    }
  }
  const breadcrumbs = event.breadcrumbs;
  if (breadcrumbs) {
    for (const crumb of breadcrumbs) {
      if (typeof crumb.message === "string") {
        crumb.message = scrubSensitiveUrl(crumb.message) ?? crumb.message;
      }
      if (crumb.data && typeof crumb.data.url === "string") {
        crumb.data.url = scrubSensitiveUrl(crumb.data.url);
      }
    }
  }
  return event;
}

/**
 * Whether the current device is a small-screen (mobile) client. Session
 * Replay's continuous DOM-mutation recording and the JS self-profiling API
 * both compete with the user's taps for main-thread time — on mid-range
 * phones that showed up as multi-second interaction lag (e.g. the mobile
 * nav hamburger, the login form). Skip both on mobile; keep error capture
 * and tracing everywhere.
 */
function isSmallScreen(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(max-width: 767px)").matches;
}

/** requestIdleCallback with a setTimeout fallback (older Safari). */
function whenIdle(run: () => void, timeoutMs = 5000) {
  const ric = (
    window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    }
  ).requestIdleCallback;
  if (typeof ric === "function") {
    ric(run, { timeout: timeoutMs });
  } else {
    window.setTimeout(run, 2000);
  }
}

/**
 * Initialize Sentry. Called once from main.tsx before React boots so we
 * capture errors during initial render and module evaluation.
 *
 * Set VITE_SENTRY_DSN as a Heroku config var to enable in production.
 * Leave it unset in local dev to skip Sentry entirely.
 *
 * Bundle note: only error capture + tracing are initialized eagerly. The
 * Session Replay (rrweb) and browser-profiling integrations are the two
 * heaviest pieces of the Sentry SDK (~270 KB minified together) and used to
 * be part of the entry chunk, which every visitor — including the login
 * page — had to download and parse before first render. They are now
 * code-split into a lazy chunk (see perfExtras.ts) that loads at idle
 * time, and skipped entirely on mobile where their runtime cost (continuous
 * DOM-mutation recording, self-profiling) caused visible interaction lag.
 *
 * The deferred chunk is optional: a fetch failure (stale deploy hash, flaky
 * network, or an ad blocker matching a previous `sentryHeavy-*.js` name)
 * must never surface as an unhandled rejection / Sentry issue. Catch and
 * skip — the app already runs with core Sentry (errors + tracing) only.
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_APP_ENV ?? import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE ?? import.meta.env.APP_VERSION,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: import.meta.env.MODE === "production" ? 0.1 : 1.0,
    profileSessionSampleRate: Number(
      import.meta.env.VITE_SENTRY_PROFILE_SESSION_SAMPLE_RATE ??
        (import.meta.env.MODE === "production" ? "0.1" : "1.0"),
    ),
    profileLifecycle: "trace",
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
    // beforeSend policy chain (first match wins by returning null / mutated
    // event):
    //  1. Drop third-party / extension noise (circular-DOM-JSON from
    //     monkey-patched appendChild — ZENYT-PLATFORM-FRONTEND-PROD-A; Web3
    //     wallet provider-disconnect rejections — PROD-B; React
    //     removeChild/insertBefore NotFoundError from Translate/extensions —
    //     PROD-C). See thirdPartyNoise.ts.
    //  2. Chunk-load policy: drop self-healing `recovered` group + pin a
    //     deploy-stable fingerprint on ungrouped dynamic-import failures.
    //     See chunkLoad.ts.
    //  3. Scrub bearer/query tokens that may linger in request URLs after
    //     OAuth/password-reset/invite callbacks.
    beforeSend(event) {
      const afterNoise = processThirdPartyNoiseEvent(event);
      if (afterNoise === null) return null;
      const afterChunk = processChunkLoadEvent(afterNoise);
      if (afterChunk === null) return null;
      return scrubSentryEventUrls(afterChunk);
    },

  });

  // Replay + profiling: desktop only, loaded once the main thread is idle so
  // they never compete with first render or the user's first interaction.
  // Trade-off: an error thrown before the deferred chunk finishes loading
  // won't have a replay attached — acceptable vs. slowing down every load.
  // Failures are swallowed: this enhancement must not page Sentry when the
  // chunk is blocked or missing (ZENYT-PLATFORM-FRONTEND-PROD-D).
  if (!isSmallScreen()) {
    whenIdle(() => {
      void import("@/app/perfExtras")
        .then((m) => m.addHeavySentryIntegrations())
        .catch(() => {
          // Optional: no Session Replay / profiling this session.
        });
    });
  }

  captureSlowPageLoad();
}

export function captureSlowOperation(
  name: string,
  durationMs: number,
  thresholdMs: number,
  context: SlowOperationContext = {},
) {
  const critical = durationMs >= 10_000;
  Sentry.withScope((scope) => {
    scope.setLevel(critical ? "error" : "warning");
    scope.setTag("slow_operation", "true");
    scope.setTag("operation", name);
    scope.setTag("critical_slow_operation", String(critical));
    scope.setContext("slow_operation", {
      duration_ms: Math.round(durationMs),
      threshold_ms: thresholdMs,
      ...context,
    });
    Sentry.captureMessage(`Slow operation: ${name}`);
  });
}

function captureSlowPageLoad() {
  if (typeof window === "undefined" || typeof performance === "undefined") return;
  window.addEventListener(
    "load",
    () => {
      const navigation = performance.getEntriesByType("navigation")[0] as
        | PerformanceNavigationTiming
        | undefined;
      const durationMs = navigation?.duration ?? performance.now();
      const thresholdMs = Number(import.meta.env.VITE_SENTRY_SLOW_PAGE_LOAD_THRESHOLD_MS ?? "3000");
      if (durationMs >= thresholdMs) {
        captureSlowOperation("platform_frontend_page_load", durationMs, thresholdMs, {
          path: window.location.pathname,
        });
      }
    },
    { once: true },
  );
}
